import { useParams } from 'react-router-dom'
import { Divider, Grid } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'

import {
  FormikInput,
  FormikScrollOnError,
  getFieldNames,
  GridRowColumn,
  Text,
} from '@/componentsV1/BaseComponents'
import {
  TaxListQuestionId,
  useTaxListQuestionRes,
} from '@/features/taxes/annual-taxes/tax-checklist/service'
import { useReselector } from '@/utils/sharedHooks'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { selectIsMarriedFilingJointlyOrQualifyingWidow } from '../../taxChecklist.selectors'
import { filterNulls } from '@/utils/typeHelpers'
import { IncomeAndLiabilitiesStepProps } from '.'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import { SubStepIdentifiers } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/stepProgress.helpers'
import UploadDocumentSubSection from '@/features/taxes/annual-taxes/tax-checklist/documents/UploadDocumentSubSection'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import {
  tqBigSpace,
  tqSmallSpace,
} from '@/features/taxes/annual-taxes/tax-checklist/helpers'
import SkipQuestion, {
  skipQuestionId,
  useSkipQuestion,
} from '@/features/taxes/annual-taxes/tax-checklist/shared/SkipQuestion'

export const outsideW2QuestionIds = [
  TaxListQuestionId.number_of_w2s_outside_practice,
  TaxListQuestionId.spouse_number_of_w2s,
]

export const OutsideW2Panel = ({
  goBack,
  goToNextStep,
  previousScreen,
  nextScreen,
}: IncomeAndLiabilitiesStepProps) => {
  const { formId: formIdParam } = useParams()
  const formId = Number(formIdParam)

  const numberOfW2sOutsideOfPracticeQR = useTaxListQuestionRes({
    questionId: TaxListQuestionId.number_of_w2s_outside_practice,
    formId,
  })

  const spouseNumberOfW2sQR = useTaxListQuestionRes({
    questionId: TaxListQuestionId.spouse_number_of_w2s,
    formId,
  })

  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const needsSpouseW2 = useReselector(
    selectIsMarriedFilingJointlyOrQualifyingWidow,
    TaxListQuestionId.filing_status,
    taxYear
  )

  const skippedQuestions = useSkipQuestion(
    needsSpouseW2
      ? outsideW2QuestionIds
      : [TaxListQuestionId.number_of_w2s_outside_practice]
  )

  const formik = useFormik({
    initialValues: {
      [TaxListQuestionId.number_of_w2s_outside_practice]:
        numberOfW2sOutsideOfPracticeQR.initialResponse,
      [TaxListQuestionId.spouse_number_of_w2s]:
        spouseNumberOfW2sQR.initialResponse,
      [skipQuestionId]: skippedQuestions.initialValue,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const responseData = filterNulls([
        numberOfW2sOutsideOfPracticeQR.getResponseData(values),
        needsSpouseW2 ? spouseNumberOfW2sQR.getResponseData(values) : null,
      ])

      await goToNextStep(responseData, nextScreen ?? null, {
        completedSteps: [SubStepIdentifiers.outsideW2s],
      })
    },
  })

  const { submitForm, isSubmitting, values } = formik

  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <FormikScrollOnError />
      <Grid>
        <GridRowColumn {...tqBigSpace}>
          <Text as="display2" textAlign="center">
            {needsSpouseW2
              ? 'Non-practice W-2 income or income from spouse'
              : 'Non-practice W-2 income'}
          </Text>
        </GridRowColumn>
        <GridRowColumn {...tqBigSpace}>
          <Text as="bodyLg">
            {needsSpouseW2
              ? 'Enter the number of non-practice W-2s you or your spouse received. Leave blank or "0" if this does not apply.'
              : 'Enter the number of non-practice W-2s you received. Leave blank or "0" if this does not apply.'}
          </Text>
        </GridRowColumn>
        <Grid.Row />
        <GridRowColumn {...tqSmallSpace}>
          <FormikInput
            name={fieldNames[TaxListQuestionId.number_of_w2s_outside_practice]}
            label={numberOfW2sOutsideOfPracticeQR.label}
            description="E.g. working for a group practice, hospital, government agency, etc."
            componentType="number"
            prefix=""
            placeholder="Enter number of W-2s if applicable"
            decimalScale={0}
            fullWidth
          />
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace} short>
          <SkipQuestion
            questionId={TaxListQuestionId.number_of_w2s_outside_practice}
          />
        </GridRowColumn>
        {needsSpouseW2 && (
          <>
            <GridRowColumn {...tqSmallSpace}>
              <FormikInput
                name={fieldNames[TaxListQuestionId.spouse_number_of_w2s]}
                label={spouseNumberOfW2sQR.label}
                componentType="number"
                prefix=""
                placeholder="Enter number of W-2s if applicable"
                decimalScale={0}
                fullWidth
              />
            </GridRowColumn>
            <GridRowColumn {...tqSmallSpace} short>
              <SkipQuestion
                questionId={TaxListQuestionId.spouse_number_of_w2s}
              />
            </GridRowColumn>
          </>
        )}

        <Grid.Row />
        <GridRowColumn>
          <Divider />
        </GridRowColumn>

        <UploadDocumentSubSection
          categories={[
            values[TaxListQuestionId.number_of_w2s_outside_practice] &&
            !(['0', 0] as (string | number | boolean | string[])[]).includes(
              values[TaxListQuestionId.number_of_w2s_outside_practice]
            )
              ? UserDocumentCategoryIdentifier.nonPracticeW2s
              : undefined,
            needsSpouseW2 &&
            values[TaxListQuestionId.spouse_number_of_w2s] &&
            !(['0', 0] as (string | number | boolean | string[])[]).includes(
              values[TaxListQuestionId.spouse_number_of_w2s]
            )
              ? UserDocumentCategoryIdentifier.formW2Spouse
              : undefined,
          ]}
        />
        <Grid.Row />
        <FormFlowFooter
          continueDisabled={isSubmitting}
          onBack={() => goBack(previousScreen ?? null)}
          onForward={submitForm}
          loading={isSubmitting}
        />
      </Grid>
    </FormikProvider>
  )
}
